/**
 * Output mode system for non-TTY / JSON support.
 *
 * Resolves once at startup, drives output formatting only.
 * In JSON mode: structured JSON to stdout, structured errors to stderr.
 * In human mode: chalk-formatted output (existing behavior).
 */

import chalk from 'chalk';
import { CliExit } from './cli-exit.js';
import { resolveErrorCode } from './exit-codes.js';
import { formatTable, type TableColumn } from './table.js';
import type { RecoveryHints } from './recovery-hints.js';
import type { InteractionModeInfo } from './interaction-mode.js';

export type OutputMode = 'human' | 'json';

let currentMode: OutputMode = 'human';

/**
 * Resolve the output mode based on flags and environment.
 *
 * Priority:
 * 1. Explicit --json flag
 * 2. WORKOS_FORCE_TTY env var → human output compatibility
 * 3. WORKOS_NO_PROMPT legacy compatibility → json
 * 4. Non-TTY auto-detection → json
 * 5. Default → human
 */
export function resolveOutputMode(jsonFlag?: boolean): OutputMode {
  if (jsonFlag) return 'json';
  if (process.env.WORKOS_FORCE_TTY === '1' || process.env.WORKOS_FORCE_TTY === 'true') return 'human';
  if (process.env.WORKOS_NO_PROMPT === '1' || process.env.WORKOS_NO_PROMPT === 'true') return 'json';
  if (!process.stdout.isTTY) return 'json';
  return 'human';
}

export function resolveEffectiveOutputMode(mode: OutputMode, interaction: InteractionModeInfo): OutputMode {
  if (interaction.mode === 'human' || interaction.source === 'non_tty') {
    return mode;
  }
  return 'json';
}

export function setOutputMode(mode: OutputMode): void {
  currentMode = mode;
  if (mode === 'json') {
    chalk.level = 0;
  }
}

export function getOutputMode(): OutputMode {
  return currentMode;
}

export function isJsonMode(): boolean {
  return currentMode === 'json';
}

/** Write structured JSON to stdout (one line, no pretty-print). */
export function outputJson(data: unknown): void {
  console.log(JSON.stringify(data));
}

/** Write a success result — chalk in human mode, JSON in json mode. */
export function outputSuccess(
  message: string,
  data?: object,
  options?: { warnings?: Array<{ code: string; message: string }> },
): void {
  if (currentMode === 'json') {
    const result: Record<string, unknown> = { status: 'ok', message };
    if (data) result.data = data;
    if (options?.warnings?.length) result.warnings = options.warnings;
    console.log(JSON.stringify(result));
  } else {
    console.log(chalk.green(message));
    if (data) {
      console.log(JSON.stringify(data, null, 2));
    }
    if (options?.warnings?.length) {
      for (const w of options.warnings) {
        console.error(chalk.yellow(w.message));
      }
    }
  }
}

export interface StructuredError {
  code: string;
  message: string;
  details?: unknown;
  /**
   * Optional structured recovery metadata for agents.
   *
   * Only include for deterministic recovery paths. Human output prints the
   * first hint as a follow-up line; JSON output serializes the full structure.
   */
  recovery?: RecoveryHints;
}

/** Write a structured error to stderr. */
export function outputError(error: StructuredError): void {
  if (currentMode === 'json') {
    console.error(JSON.stringify({ error }));
  } else {
    console.error(chalk.red(error.message));
    const firstHint = error.recovery?.hints[0];
    if (firstHint) {
      const suffix = firstHint.command ? ` Run: ${firstHint.command}` : '';
      console.error(chalk.dim(`→ ${firstHint.description}${suffix}`));
    }
  }
}

/** Write tabular data — chalk table in human mode, JSON array in json mode. */
export function outputTable(columns: TableColumn[], rows: string[][], rawData?: unknown[]): void {
  if (currentMode === 'json') {
    if (rawData) {
      console.log(JSON.stringify(rawData));
    } else {
      const headers = columns.map((c) => c.header);
      const jsonRows = rows.map((row) => {
        const obj: Record<string, string> = {};
        headers.forEach((h, i) => {
          obj[h] = row[i] ?? '';
        });
        return obj;
      });
      console.log(JSON.stringify(jsonRows));
    }
  } else {
    console.log(formatTable(columns, rows));
  }
}

export function exitWithError(
  error: StructuredError & {
    apiContext?: { status?: number; code?: string; resource?: string };
  },
): never {
  outputError(error);
  const { reason: codeReason, exit } = resolveErrorCode(error.code);
  const reason = error.apiContext && codeReason === 'validation_error' ? 'api_error' : codeReason;
  throw new CliExit(exit, {
    reason,
    errorCode: error.code,
    apiContext: error.apiContext,
  });
}
